// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Pure derivation for the fleet SOP governance screen: twin entities in,
// coverage out; SOP Factory summaries in, register rows out. No fetching and no
// formatting here, so every rule below is testable against a literal payload.

import type { ArtifactSummary } from '../types/sopFactory';
import type { Entity } from '../types/digitalTwin';
import { assetName } from '../utils/entityLabels';
import type {
  CoverageResult,
  CoverageTone,
  EquipmentTypeGap,
  SiteCoverage,
  SopBinding,
  SopCoverageRollup,
  SopRegisterRow,
  UnboundEquipment,
} from '../types/sopGovernance';

/**
 * Entity types that can carry a bound SOP.
 *
 * Derived from the ontology rather than chosen by hand: these are exactly the
 * types whose `*Attributes` schema declares `hasSOP` in
 * `docs/upstream-specs/data-model-schema.json`. `sopGovernanceSelectors.test.ts`
 * re-derives the list from that schema and fails if the two drift, so an
 * ontology sync that adds or removes a bindable type is caught here rather than
 * silently changing the denominator.
 */
export const SOP_BINDABLE_EQUIPMENT_TYPES: readonly string[] = [
  'ATSType',
  'BESSType',
  'BMSType',
  'BuswayType',
  'ControllerType',
  'FeederType',
  'GatewayType',
  'GeneratorSetType',
  'GroundingNetworkType',
  'ITRackType',
  'MCCType',
  'MotorFeederType',
  'PDUType',
  'PQMeterType',
  'PowerDeviceType',
  'RPPType',
  'RackPDUType',
  'SPDType',
  'STSType',
  'SwitchgearType',
  'TransformerType',
  'UPSType',
  'UtilityMeterType',
];

const BINDABLE = new Set(SOP_BINDABLE_EQUIPMENT_TYPES);

/** Whether this entity is equipment that could carry a bound SOP. */
export function isBindableEquipment(entity: Entity): boolean {
  return BINDABLE.has(entity.type);
}

/**
 * Binding state of one equipment entity.
 *
 * `hasSOP` is a `ValueEnvelope` whose `value` the ontology leaves untyped, so
 * this only asks whether a value is present and non-empty — it deliberately
 * does not try to parse an id, list or URL out of it. An absent attribute reads
 * as `unknown` rather than `unbound`: at fleet level `hasAnyBinding` decides
 * whether the source publishes binding data at all.
 */
export function bindingOf(entity: Entity): SopBinding {
  const envelope = entity.attributes?.hasSOP as { value?: unknown } | undefined;
  if (envelope === undefined || envelope === null) return 'unknown';
  const value = typeof envelope === 'object' && 'value' in envelope ? envelope.value : envelope;
  if (value === undefined || value === null) return 'unbound';
  if (typeof value === 'string' && value.trim() === '') return 'unbound';
  if (Array.isArray(value) && value.length === 0) return 'unbound';
  return 'bound';
}

/** True when at least one equipment entity carries a `hasSOP` attribute. */
export function hasAnyBinding(entities: readonly Entity[]): boolean {
  return entities.some(e => isBindableEquipment(e) && bindingOf(e) !== 'unknown');
}

/** The equipment's name. Never its id — see `assetName`. */
export function entityLabel(entity: Entity): string {
  return assetName(entity, entity.id);
}

export interface SiteGraph {
  siteId: string;
  siteKey: string;
  label: string;
  entities: readonly Entity[];
}

/**
 * Coverage for one site.
 *
 * The denominator is every SOP-bindable equipment entity the twin returned. The
 * design binding says "active equipment", but the ontology has no lifecycle or
 * active flag on equipment attributes, so that filter cannot be applied — see
 * the PR description. Equipment whose binding is `unknown` counts as unbound
 * here; whether that number means anything is `hasAnyBinding`'s call, made once
 * for the whole fleet.
 */
export function buildSiteCoverage(graph: SiteGraph): SiteCoverage {
  const equipment = graph.entities.filter(isBindableEquipment);
  const outstandingByType = new Map<string, number>();
  let bound = 0;

  for (const entity of equipment) {
    if (bindingOf(entity) === 'bound') {
      bound += 1;
    } else {
      outstandingByType.set(entity.type, (outstandingByType.get(entity.type) ?? 0) + 1);
    }
  }

  const outstanding: EquipmentTypeGap[] = Array.from(outstandingByType, ([type, count]) => ({
    equipmentType: type,
    count,
  })).sort((a, b) => b.count - a.count || a.equipmentType.localeCompare(b.equipmentType));

  return {
    siteId: graph.siteId,
    siteKey: graph.siteKey,
    label: graph.label,
    total: equipment.length,
    bound,
    // A site with no bindable equipment is fully covered, not a divide by zero.
    pct: equipment.length === 0 ? 100 : Math.round((bound / equipment.length) * 100),
    outstanding,
  };
}

/** Every unbound equipment entity at a site, as read-only compliance gaps. */
export function unboundEquipmentAt(graph: SiteGraph): UnboundEquipment[] {
  return graph.entities
    .filter(entity => isBindableEquipment(entity) && bindingOf(entity) !== 'bound')
    .map(entity => ({
      entityId: entity.id,
      label: entityLabel(entity),
      equipmentType: entity.type,
      siteId: graph.siteId,
      siteLabel: graph.label,
    }));
}

/**
 * Roll every site up into the fleet figure, or report that binding is
 * unpublished when not one site's equipment carries `hasSOP`. Reporting 0%
 * would claim a measurement nobody made.
 */
export function buildCoverage(graphs: readonly SiteGraph[]): CoverageResult {
  const anyPublished = graphs.some(graph => hasAnyBinding(graph.entities));
  if (!anyPublished) {
    // The denominator survives: the twin did return equipment, it just said
    // nothing about binding. Carrying the counts lets the coverage table keep
    // its rows and name the hole per site.
    const unmeasured = graphs.map(graph => ({
      siteId: graph.siteId,
      siteKey: graph.siteKey,
      label: graph.label,
      total: graph.entities.filter(isBindableEquipment).length,
    }));
    return {
      state: 'unpublished',
      sites: unmeasured,
      total: unmeasured.reduce((sum, site) => sum + site.total, 0),
    };
  }

  const sites = graphs.map(buildSiteCoverage);
  const total = sites.reduce((sum, site) => sum + site.total, 0);
  const bound = sites.reduce((sum, site) => sum + site.bound, 0);

  const rollup: SopCoverageRollup = {
    sites,
    total,
    bound,
    pct: total === 0 ? 100 : Math.round((bound / total) * 100),
    equipmentWithoutSop: total - bound,
    unbound: graphs.flatMap(unboundEquipmentAt),
  };
  return { state: 'measured', rollup };
}

/**
 * Coverage tone. Only 100% reads as healthy; below `faultBelow` reads as a
 * fault. The fleet band is tighter than a site's because one unbound item
 * barely dents a fleet-wide percentage.
 */
export function coverageTone(pct: number, faultBelow = 90): CoverageTone {
  if (pct === 100) return 'ok';
  return pct >= faultBelow ? 'warn' : 'fault';
}

/** Fleet-level tone (95% floor). */
export function fleetCoverageTone(pct: number): CoverageTone {
  return coverageTone(pct, 95);
}

/**
 * Project a SOP Factory list item onto a register row.
 *
 * Every field on `ArtifactSummary` is optional in our relay DTO (the BFF is a
 * resilient relay), so the projection fills only what upstream sent: an untitled
 * artifact shows its id rather than an empty cell, and an absent status stays
 * absent instead of being guessed at.
 */
export function toRegisterRow(summary: ArtifactSummary): SopRegisterRow {
  return {
    artifactId: summary.artifact_id,
    title: summary.title?.trim() || summary.artifact_id,
    version: summary.version ?? '',
    status: summary.status ?? '',
    equipmentType: summary.categories?.equipment_type,
    domain: summary.categories?.domain,
    riskTier: summary.categories?.risk_tier,
  };
}

/** Register rows, id-sorted so the table order is stable across refetches. */
export function toRegisterRows(items: readonly ArtifactSummary[]): SopRegisterRow[] {
  return items.map(toRegisterRow).sort((a, b) => a.artifactId.localeCompare(b.artifactId));
}

/** Count register rows per upstream status, for the register tiles. */
export function countByStatus(rows: readonly SopRegisterRow[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const row of rows) {
    const key = row.status || 'unknown';
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}
